import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Crown, Check, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// withCredentials so the httpOnly session cookie is sent on every auth call.
const authApi = axios.create({ baseURL: API, withCredentials: true, timeout: 20000 });

export async function exchangeSession(sessionId) {
  // Attach a stored referral code (from ?ref=) so the backend can link the new
  // account to its referrer on first signup.
  let ref = null;
  try { ref = localStorage.getItem("vspo_ref"); } catch (e) { console.debug("ref lecture:", e); }
  const { data } = await authApi.post("/auth/session", { session_id: sessionId, ref: ref || undefined });
  try { if (ref) localStorage.removeItem("vspo_ref"); } catch (e) { console.debug("ref nettoyage:", e); }
  return data;
}
export async function fetchMe() {
  const { data } = await authApi.get("/auth/me");
  return data;
}
export async function logoutRequest() {
  const { data } = await authApi.post("/auth/logout", {});
  return data;
}
export async function fetchPlans() {
  const { data } = await authApi.get("/premium/plans");
  return data.plans || [];
}
export async function createCheckout(lookupKey) {
  const { data } = await authApi.post("/payments/checkout", {
    lookup_key: lookupKey,
    origin_url: window.location.origin,
  });
  return data;
}
export async function fetchPaymentStatus(sessionId) {
  const { data } = await authApi.get(`/payments/status/${sessionId}`);
  return data;
}
export async function openPortal() {
  const { data } = await authApi.post("/payments/portal", {
    lookup_key: "",
    origin_url: window.location.origin,
  });
  return data;
}

export async function fetchReferral() {
  const { data } = await authApi.get("/premium/referral");
  return data; // { code, referral_count, free_months_total, pending_free_months }
}

export async function fetchZones() {
  const { data } = await authApi.get("/premium/zones");
  return data.zones || [];
}
export async function addZone(zone) {
  const { data } = await authApi.post("/premium/zones", zone);
  return data;
}
export async function deleteZone(zoneId) {
  const { data } = await authApi.delete(`/premium/zones/${zoneId}`);
  return data;
}

export async function exportAccount() {
  const { data } = await authApi.get("/account/export");
  return data;
}
export async function deleteAccount() {
  const { data } = await authApi.delete("/account");
  return data;
}

// Start the Google (Emergent) login. redirect lands back on /compte.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function startLogin() {
  const redirectUrl = window.location.origin + "/compte";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const PremiumContext = createContext(null);
export const usePremium = () => useContext(PremiumContext);

export function PremiumProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paywall, setPaywall] = useState({ open: false, feature: "" });

  const refresh = useCallback(async () => {
    try {
      const d = await fetchMe();
      setUser(d);
    } catch (e) {
      setUser({ authenticated: false, premium_active: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Capture a referral code from the URL (?ref=CODE) so it survives until the
    // visitor signs in (then it's sent once during session exchange).
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref) localStorage.setItem("vspo_ref", ref.slice(0, 32));
    } catch (e) {
      console.debug("Capture ref échouée:", e);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth, AuthCallback exchanges the session first — skip.
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const isPremium = !!(user && user.premium_active);

  // Returns true if the user may use a premium feature; otherwise opens the
  // paywall and returns false (so callers can abort the action).
  const requirePremium = useCallback(
    (feature = "") => {
      if (isPremium) return true;
      setPaywall({ open: true, feature });
      return false;
    },
    [isPremium],
  );

  const value = {
    user,
    loading,
    isAuthenticated: !!(user && user.authenticated),
    isPremium,
    refresh,
    setUser,
    requirePremium,
    openPaywall: (feature = "") => setPaywall({ open: true, feature }),
  };
  return (
    <PremiumContext.Provider value={value}>
      {children}
      <PaywallDialog
        state={paywall}
        onClose={() => setPaywall({ open: false, feature: "" })}
        authenticated={!!(user && user.authenticated)}
      />
    </PremiumContext.Provider>
  );
}

const PAYWALL_PERKS = [
  "Navigation sans publicité",
  "Voix premium + Le Génie enrichi",
  "Bornes de recharge mondiales + planification d'arrêts",
  "Itinéraires multi-arrêts illimités",
];

function PaywallDialog({ state, onClose, authenticated }) {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (state.open && plans.length === 0) {
      fetchPlans().then(setPlans).catch(() => setPlans([]));
    }
  }, [state.open, plans.length]);

  const subscribe = async (lookupKey) => {
    if (!authenticated) {
      startLogin();
      return;
    }
    setBusy(lookupKey);
    try {
      const { checkout_url } = await createCheckout(lookupKey);
      if (checkout_url) window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer le paiement");
      setBusy("");
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="paywall-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" /> Fonctionnalité Premium
          </DialogTitle>
          <DialogDescription>
            {state.feature
              ? `« ${state.feature} » est réservé aux abonnés vspo Premium.`
              : "Cette fonctionnalité est réservée aux abonnés vspo Premium."}
          </DialogDescription>
        </DialogHeader>
        <ul className="my-1 space-y-1.5">
          {PAYWALL_PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        <div className="mt-1 flex items-center justify-center rounded-lg bg-amber-500/10 py-2 text-sm font-medium text-amber-600 dark:text-amber-400">
          7 jours d'essai gratuit, puis 0,99 €/mois
        </div>
        <div className="mt-2 grid gap-2">
          {plans.map((p) => (
            <Button
              key={p.lookup_key}
              onClick={() => subscribe(p.lookup_key)}
              disabled={!!busy}
              variant={p.lookup_key.includes("yearly") ? "outline" : "default"}
              className={p.lookup_key.includes("yearly") ? "" : "bg-amber-500 text-black hover:bg-amber-400"}
              data-testid={`paywall-subscribe-${p.lookup_key}`}
            >
              {busy === p.lookup_key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {authenticated ? "Essayer" : "Se connecter"} — {p.label} {p.price}{p.period}
            </Button>
          ))}
          <a href="/premium" className="mt-1 text-center text-xs text-muted-foreground underline">
            Voir tous les détails
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
